import { useEffect, useMemo, useRef, useState } from "react";
import {
  Image,
  Linking,
  Pressable,
  ScrollView,
  Text,
  useWindowDimensions,
  View,
} from "react-native";
import * as DocumentPicker from "expo-document-picker";
import { TABLET_BREAKPOINT } from "@src/packages/utils/common";
import {
  FileText,
  Image as ImageIcon,
  Paperclip,
  X,
} from "lucide-react-native";
import {
  ClinicalDataStatus,
  colors,
  Dialog,
  EmptyStateSubtitleText,
  EmptyStateTitleText,
  FieldMessage,
  IconButton,
  semanticColors,
  ChatComposer,
  PrimaryButton,
  TextField,
  MoonSkeleton,
} from "@louise/moon-ui";
import { useTranslations } from "@src/packages/i18n";
import {
  type SupportAttachment,
  type SupportAttachmentDraft,
  type SupportMessage,
  useSupportConversation,
} from "./hooks/useSupportConversation";

import {
  pressLabel,
  usePressCapture,
} from "@src/packages/analytics/usePressCapture";
const MAX_ATTACHMENTS = 5;
const MAX_ATTACHMENT_BYTES = 10 * 1024 * 1024;

type Props = {
  visible: boolean;
  entryPoint?: "support" | "feedback";
  onClose: () => void;
};

function AttachmentDraftRow({
  attachment,
  onRemove,
}: {
  attachment: SupportAttachmentDraft;
  onRemove: () => void;
}) {
  const Icon = attachment.mimeType?.startsWith("image/") ? ImageIcon : FileText;

  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 8,
        paddingVertical: 8,
        borderTopWidth: 1,
        borderTopColor: semanticColors.border.subtle,
      }}
    >
      <Icon size={15} color={colors.textLight} strokeWidth={1.8} />
      <Text
        className="font-dm-sans"
        numberOfLines={1}
        style={{ flex: 1, color: colors.textMid, fontSize: 12 }}
      >
        {attachment.name}
      </Text>
      <IconButton
        accessibilityLabel={`Remove ${attachment.name}`}
        action="remove_attachment"
        onPress={onRemove}
        hitSlop={10}
        size={15}
        pressedOpacity={1}
      >
        <X size={15} color={colors.textFaint} strokeWidth={1.8} />
      </IconButton>
    </View>
  );
}

function isImageAttachment(attachment: SupportAttachment) {
  return attachment.contentType?.startsWith("image/") === true;
}

function ImageAttachmentPreview({
  attachment,
  marginTop,
}: {
  attachment: SupportAttachment;
  marginTop: number;
}) {
  const capturePress = usePressCapture("ImageAttachmentPreview");
  // Chatwoot's thumb_url is a square, center-cropped representation. Use the
  // original data_url for the inline preview so the entire image remains
  // visible, matching Chatwoot's own conversation UI.
  const previewUrl = attachment.url ?? attachment.thumbnailUrl;
  if (!previewUrl) return null;

  const aspectRatio =
    attachment.width && attachment.height
      ? attachment.width / attachment.height
      : 4 / 3;
  const fullSizeUrl = attachment.url;
  const image = (
    <Image
      source={{ uri: previewUrl }}
      accessibilityLabel={attachment.name ?? "Image attachment"}
      resizeMode="contain"
      style={{
        width: "100%",
        aspectRatio,
        maxHeight: 320,
        borderRadius: 10,
        backgroundColor: "rgba(117, 78, 46, 0.06)",
      }}
    />
  );

  return fullSizeUrl ? (
    <Pressable
      onPress={() => {
        capturePress(
          pressLabel(attachment.name ?? "Open image attachment"),
          "open_image_attachment",
        );
        void Linking.openURL(fullSizeUrl);
      }}
      accessibilityRole="link"
      accessibilityLabel={attachment.name ?? "Open image attachment"}
      style={{ width: "100%", marginTop }}
    >
      {image}
    </Pressable>
  ) : (
    <View style={{ width: "100%", marginTop }}>{image}</View>
  );
}

function MessageBubble({
  message,
  patientLabel,
  teamLabel,
}: {
  message: SupportMessage;
  patientLabel: string;
  teamLabel: string;
}) {
  const capturePress = usePressCapture("SupportMessageBubble");
  const isTeam = message.direction === "team";
  const hasImagePreview = message.attachments.some(
    (attachment) =>
      isImageAttachment(attachment) &&
      Boolean(attachment.thumbnailUrl ?? attachment.url),
  );

  return (
    <View
      style={{
        alignSelf: isTeam ? "flex-start" : "flex-end",
        width: hasImagePreview ? "88%" : undefined,
        maxWidth: "88%",
      }}
    >
      <Text
        className="font-dm-sans"
        style={{
          alignSelf: isTeam ? "flex-start" : "flex-end",
          color: colors.textFaint,
          fontSize: 10.5,
          fontWeight: "500",
          marginBottom: 4,
          paddingHorizontal: 3,
        }}
      >
        {isTeam ? teamLabel : patientLabel}
      </Text>
      <View
        style={{
          paddingHorizontal: 12,
          paddingVertical: 9,
          borderRadius: 14,
          borderBottomLeftRadius: isTeam ? 4 : 14,
          borderBottomRightRadius: isTeam ? 14 : 4,
          backgroundColor: isTeam
            ? "rgba(255, 255, 255, 0.62)"
            : "rgba(117, 78, 46, 0.10)",
          borderWidth: isTeam ? 1 : 0,
          borderColor: isTeam ? semanticColors.border.subtle : "transparent",
        }}
      >
        {message.content ? (
          <Text
            className="font-dm-sans"
            style={{
              color: colors.text,
              fontSize: 13,
              lineHeight: 18,
            }}
          >
            {message.content}
          </Text>
        ) : null}
        {message.attachments.map((attachment, index) => {
          const marginTop = message.content || index > 0 ? 7 : 0;
          if (
            isImageAttachment(attachment) &&
            (attachment.thumbnailUrl ?? attachment.url)
          ) {
            return (
              <ImageAttachmentPreview
                key={attachment.id}
                attachment={attachment}
                marginTop={marginTop}
              />
            );
          }

          const content = (
            <View
              style={{
                flexDirection: "row",
                alignItems: "center",
                gap: 6,
                marginTop,
              }}
            >
              <FileText size={14} color={colors.textLight} strokeWidth={1.8} />
              <Text
                className="font-dm-sans"
                numberOfLines={1}
                style={{
                  color: colors.textLight,
                  fontSize: 11.5,
                  textDecorationLine: attachment.url ? "underline" : "none",
                }}
              >
                {attachment.name ?? "Attachment"}
              </Text>
            </View>
          );

          return attachment.url ? (
            <Pressable
              key={attachment.id}
              onPress={() => {
                capturePress(
                  pressLabel(attachment.name ?? "Open attachment"),
                  "open_attachment",
                );
                void Linking.openURL(attachment.url!);
              }}
              accessibilityRole="link"
              accessibilityLabel={attachment.name ?? "Open attachment"}
            >
              {content}
            </Pressable>
          ) : (
            <View key={attachment.id}>{content}</View>
          );
        })}
      </View>
    </View>
  );
}

function ConversationLoadingSkeleton() {
  return (
    <View
      accessibilityLabel="Loading support conversation"
      style={{ gap: 12, paddingVertical: 8 }}
    >
      <MoonSkeleton width="68%" height={50} radius={14} />
      <MoonSkeleton
        width="48%"
        height={42}
        radius={14}
        style={{ alignSelf: "flex-end" }}
      />
      <MoonSkeleton width="57%" height={36} radius={14} />
    </View>
  );
}

function ConversationEmptyState({
  title,
  body,
}: {
  title: string;
  body: string;
}) {
  return (
    <View
      testID="support-chat-empty-state"
      style={{
        alignItems: "center",
        justifyContent: "center",
        minHeight: 116,
        paddingHorizontal: 20,
        paddingVertical: 18,
      }}
    >
      <View style={{ alignItems: "center", gap: 4 }}>
        <EmptyStateTitleText style={{ textAlign: "center" }}>
          {title}
        </EmptyStateTitleText>
        <EmptyStateSubtitleText style={{ textAlign: "center", marginTop: 0 }}>
          {body}
        </EmptyStateSubtitleText>
      </View>
    </View>
  );
}

type ComposerProps = {
  compact: boolean;
  autoFocus: boolean;
  attachments: SupportAttachmentDraft[];
  attachmentCountText: string;
  canAttachMore: boolean;
  canSend: boolean;
  draft: string;
  inputLabel: string;
  placeholder: string;
  attachLabel: string;
  sendLabel: string;
  isPending: boolean;
  onChangeDraft: (value: string) => void;
  onPickAttachments: () => void;
  onRemoveAttachment: (index: number) => void;
  onSend: () => void;
};

function SupportComposer({
  compact,
  autoFocus,
  attachments,
  attachmentCountText,
  canAttachMore,
  canSend,
  draft,
  inputLabel,
  placeholder,
  attachLabel,
  sendLabel,
  isPending,
  onChangeDraft,
  onPickAttachments,
  onRemoveAttachment,
  onSend,
}: ComposerProps) {
  const capturePress = usePressCapture("SupportComposer");
  if (!compact) {
    // Sharing feedback is a short form rather than a chat: the message box,
    // any files, then Send.
    return (
      <View style={{ marginTop: 16, gap: 8 }}>
        <TextField
          notes
          value={draft}
          onChangeText={onChangeDraft}
          placeholder={placeholder}
          accessibilityLabel={inputLabel}
          autoFocus={autoFocus}
          autoCorrect
          maxLength={5_000}
          editable={!isPending}
        />
        {attachments.map((attachment, index) => (
          <AttachmentDraftRow
            key={`${attachment.uri}-${index}`}
            attachment={attachment}
            onRemove={() => onRemoveAttachment(index)}
          />
        ))}
        <View
          style={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Pressable
            accessibilityRole="button"
            accessibilityLabel={attachLabel}
            onPress={() => {
              capturePress(pressLabel(attachLabel), "pick_support_attachments");
              onPickAttachments();
            }}
            disabled={!canAttachMore || isPending}
            style={({ pressed }) => ({
              flexDirection: "row",
              alignItems: "center",
              gap: 6,
              opacity: !canAttachMore || isPending ? 0.45 : pressed ? 0.65 : 1,
              paddingVertical: 5,
              paddingRight: 6,
            })}
          >
            <Paperclip size={15} color={colors.textLight} strokeWidth={1.8} />
            <Text
              className="font-dm-sans"
              style={{ color: colors.textLight, fontSize: 12 }}
            >
              {attachLabel}
            </Text>
          </Pressable>
          <Text
            className="font-dm-sans"
            style={{ color: colors.textFaint, fontSize: 11 }}
          >
            {attachmentCountText}
          </Text>
        </View>
        <PrimaryButton
          label={sendLabel}
          onPress={onSend}
          disabled={!canSend}
          loading={isPending}
          style={{ marginTop: 8 }}
        />
      </View>
    );
  }
  return (
    <View style={{ marginTop: 14, gap: 8 }}>
      {attachments.map((attachment, index) => (
        <AttachmentDraftRow
          key={`${attachment.uri}-${index}`}
          attachment={attachment}
          onRemove={() => onRemoveAttachment(index)}
        />
      ))}
      <ChatComposer
        value={draft}
        onChangeText={onChangeDraft}
        onSend={onSend}
        canSend={canSend && !isPending}
        placeholder={placeholder}
        accessibilityLabel={inputLabel}
        autoFocus={autoFocus}
        autoCorrect
        multiline
        maxLength={5_000}
        editable={!isPending}
        sendLabel={sendLabel}
        sendAction="send_support_message"
        leading={
          <IconButton
            accessibilityLabel={attachLabel}
            action="pick_support_attachments"
            onPress={() => {
              capturePress(pressLabel(attachLabel), "pick_support_attachments");
              onPickAttachments();
            }}
            disabled={!canAttachMore || isPending}
            hitSlop={8}
            size={16}
            disabledOpacity={0.45}
          >
            <Paperclip size={16} color={colors.textLight} strokeWidth={1.8} />
          </IconButton>
        }
      />
      {attachments.length > 0 ? (
        <Text
          className="font-dm-sans"
          style={{
            color: colors.textFaint,
            fontSize: 11,
            paddingHorizontal: 16,
          }}
        >
          {attachmentCountText}
        </Text>
      ) : null}
    </View>
  );
}

/**
 * Louise-owned support UX. Support opens as a conversation (with its durable
 * history), while feedback stays a short direct submission that writes to that
 * same conversation.
 */
export function SupportConversationModal({
  visible,
  entryPoint = "support",
  onClose,
}: Props) {
  const t = useTranslations();
  const isFeedback = entryPoint === "feedback";
  const { width } = useWindowDimensions();
  // A chat thread is too cramped in a centered card on a phone; present the
  // support conversation full-screen on small viewports. Feedback stays a short
  // centered form, and tablets/desktop keep the centered card.
  const fullscreen = !isFeedback && width < TABLET_BREAKPOINT;
  const { conversation, sendMessage } = useSupportConversation(
    visible && !isFeedback,
  );
  const messageScrollRef = useRef<ScrollView>(null);
  const [draft, setDraft] = useState("");
  const [attachments, setAttachments] = useState<SupportAttachmentDraft[]>([]);
  const [localError, setLocalError] = useState<string | null>(null);
  const [feedbackSent, setFeedbackSent] = useState(false);

  const canSend = Boolean(draft.trim() || attachments.length > 0);
  const messageError =
    localError ??
    (sendMessage.error instanceof Error ? sendMessage.error.message : null);
  const messages = conversation.data?.messages ?? [];
  const title = isFeedback
    ? t("you.support.message_feedback_title")
    : t("you.support.chat_title");
  const subtitle = isFeedback
    ? t("you.support.message_feedback_subtitle")
    : t("you.support.chat_subtitle");

  const canAttachMore = attachments.length < MAX_ATTACHMENTS;
  const attachmentCountText = useMemo(
    () => `${attachments.length}/${MAX_ATTACHMENTS}`,
    [attachments.length],
  );

  useEffect(() => {
    if (visible) return;
    setDraft("");
    setAttachments([]);
    setLocalError(null);
    setFeedbackSent(false);
  }, [visible]);

  useEffect(() => {
    if (isFeedback || messages.length === 0) return;
    const timeout = setTimeout(() => {
      messageScrollRef.current?.scrollToEnd({ animated: false });
    }, 0);
    return () => clearTimeout(timeout);
  }, [isFeedback, messages.length]);

  const pickAttachments = async () => {
    if (!canAttachMore || sendMessage.isPending) return;
    setLocalError(null);
    const result = await DocumentPicker.getDocumentAsync({
      type: "*/*",
      multiple: true,
      copyToCacheDirectory: true,
    });
    if (result.canceled || !result.assets?.length) return;

    const oversized = result.assets.find(
      (asset) => (asset.size ?? 0) > MAX_ATTACHMENT_BYTES,
    );
    if (oversized) {
      setLocalError(t("you.support.message_attachment_too_large"));
      return;
    }

    const room = MAX_ATTACHMENTS - attachments.length;
    const selected = result.assets.slice(0, room).map((asset) => ({
      uri: asset.uri,
      name: asset.name ?? "Attachment",
      mimeType: asset.mimeType,
      size: asset.size,
      file: asset.file,
    }));
    setAttachments((current) => [...current, ...selected]);
    if (result.assets.length > room) {
      setLocalError(t("you.support.message_too_many_attachments"));
    }
  };

  const handleSend = async () => {
    if (!canSend || sendMessage.isPending) return;
    setLocalError(null);
    try {
      await sendMessage.mutateAsync({
        content: draft,
        entryPoint,
        attachments,
      });
      setDraft("");
      setAttachments([]);
      if (isFeedback) setFeedbackSent(true);
    } catch {
      // The mutation error is intentionally surfaced inline below the
      // composer. Keeping the draft intact makes retrying one tap.
    }
  };

  const composer = (
    <SupportComposer
      compact={!isFeedback}
      autoFocus={isFeedback && visible}
      attachments={attachments}
      attachmentCountText={attachmentCountText}
      canAttachMore={canAttachMore}
      canSend={canSend}
      draft={draft}
      inputLabel={t("you.support.message_input_label")}
      placeholder={t("you.support.message_placeholder")}
      attachLabel={t("you.support.message_attach")}
      sendLabel={t("you.support.message_send")}
      isPending={sendMessage.isPending}
      onChangeDraft={(value) => {
        setDraft(value);
        if (localError) setLocalError(null);
        if (feedbackSent) setFeedbackSent(false);
      }}
      onPickAttachments={pickAttachments}
      onRemoveAttachment={(index) =>
        setAttachments((current) =>
          current.filter((_, itemIndex) => itemIndex !== index),
        )
      }
      onSend={handleSend}
    />
  );

  const content = (
    <>
      <Text
        accessibilityRole="header"
        className="font-dm-sans"
        style={{
          color: colors.text,
          fontSize: 20,
          lineHeight: 25,
          fontWeight: "600",
          paddingRight: 28,
        }}
      >
        {title}
      </Text>
      <Text
        className="font-dm-sans"
        style={{
          color: colors.textLight,
          fontSize: 13,
          lineHeight: 18,
          marginTop: 5,
        }}
      >
        {subtitle}
      </Text>

      {isFeedback ? (
        <>
          {composer}
          {feedbackSent ? (
            <Text
              className="font-dm-sans"
              style={{
                color: colors.textLight,
                fontSize: 12,
                lineHeight: 17,
                marginTop: 10,
              }}
            >
              {t("you.support.message_feedback_sent")}
            </Text>
          ) : null}
        </>
      ) : (
        <>
          <View
            accessibilityLabel={t("you.support.chat_history_label")}
            style={
              fullscreen
                ? { flex: 1, marginTop: 16 }
                : {
                    maxHeight: 236,
                    minHeight: conversation.isPending ? 132 : undefined,
                    marginTop: 16,
                  }
            }
          >
            {conversation.isPending ? (
              <ConversationLoadingSkeleton />
            ) : conversation.isError && messages.length === 0 ? (
              <ClinicalDataStatus
                state="unavailable"
                title={t("you.support.chat_load_error")}
                message={t("states.check_connection_and_try_again")}
                retryLabel={t("clinical_data.retry")}
                retryingLabel={t("clinical_data.retrying")}
                onRetry={() => void conversation.refetch()}
                isRetrying={conversation.isFetching}
                embedded
                testID="support-chat-unavailable"
              />
            ) : messages.length > 0 ? (
              <ScrollView
                ref={messageScrollRef}
                style={fullscreen ? { flex: 1 } : { maxHeight: 236 }}
                contentContainerStyle={{ gap: 12, paddingRight: 2 }}
                showsVerticalScrollIndicator={false}
                keyboardDismissMode="on-drag"
                keyboardShouldPersistTaps="handled"
                onContentSizeChange={() =>
                  messageScrollRef.current?.scrollToEnd({ animated: false })
                }
              >
                {messages.map((message) => (
                  <MessageBubble
                    key={message.id}
                    message={message}
                    patientLabel={t("you.support.message_from_you")}
                    teamLabel={t("you.support.message_from_support")}
                  />
                ))}
              </ScrollView>
            ) : (
              <ConversationEmptyState
                title={t("you.support.chat_empty_title")}
                body={t("you.support.chat_empty_subtitle")}
              />
            )}
          </View>

          {composer}
        </>
      )}

      {messageError ? (
        <FieldMessage style={{ marginTop: 8 }}>{messageError}</FieldMessage>
      ) : null}
    </>
  );

  return (
    <Dialog
      visible={visible}
      onClose={onClose}
      layout={fullscreen ? "fullscreen" : "centered"}
      closeButton="minimal"
      contentStyle={
        fullscreen
          ? undefined
          : { paddingTop: 26, paddingHorizontal: 24, paddingBottom: 22 }
      }
      testID="support-conversation-modal"
    >
      {fullscreen ? <View style={{ flex: 1 }}>{content}</View> : content}
    </Dialog>
  );
}

export default SupportConversationModal;
